import {
  describeValue,
  isEnumKind,
  type CheckInTemplate,
  type Entry,
  type Tag,
  type Track,
} from '@anytracker/core'

import { trackColour } from '../../charts/colours.js'
import { formatTime } from '../../format/dates.js'
import styles from './Timeline.module.css'

interface Props {
  readonly entry: Entry
  /** Every track, archived included — see below. */
  readonly tracks: ReadonlyMap<string, Track>
  readonly tags: ReadonlyMap<string, Tag>
  readonly templates: ReadonlyMap<string, CheckInTemplate>
  readonly deviceZone: string
  readonly onOpen: () => void
}

/**
 * One recorded moment, against the day's rail (T-305, refitted in T-904).
 *
 * `tracks` must contain **archived tracks too**. Resolving against the active
 * list only is how history silently develops holes as tracks are retired — the
 * measurement is still there, and it renders as nothing at all.
 *
 * The time lives in the gutter rather than in the card, so a day's entries line
 * up against a single vertical axis and the shape of the day is visible without
 * reading any of them. The rail and dot are `aria-hidden`; the time is not, and
 * stays first in the reading order where it always was.
 */
export function EntryRow({ entry, tracks, tags, templates, deviceZone, onOpen }: Props) {
  const template =
    entry.templateId === undefined ? undefined : templates.get(entry.templateId)
  // Only worth saying when it differs — noting "Europe/London" on every entry
  // for someone who never leaves London is noise.
  const elsewhere = entry.at.timeZone !== deviceZone

  /*
   * The dot takes the first measurement's track colour.
   *
   * Arbitrary but stable: measurements follow template order, so the same
   * check-in reads the same colour all the way down the page. It is a
   * convention for scanning, not a meaning — it says nothing about the values.
   */
  const first = entry.measurements[0]
  const dot = first === undefined ? 'var(--border-strong)' : trackColour(first.trackId)

  return (
    <div className={styles['rail']}>
      <span className={styles['railTime']}>{formatTime(entry.at)}</span>
      <span className={styles['railLine']} aria-hidden="true" />
      <span
        className={styles['railDot']}
        style={{ boxShadow: `0 0 0 2.5px ${dot}` }}
        aria-hidden="true"
      />

      <button type="button" className={styles['entry']} onClick={onOpen}>
        <span className={styles['entryHeader']}>
          {template !== undefined && (
            <span className={styles['entryTemplate']}>{template.name}</span>
          )}
          {elsewhere && <span className={styles['entryZone']}>{entry.at.timeZone}</span>}
          {entry.note !== undefined && (
            <span
              className={styles['entryNote']}
              title="Has a note"
              aria-label="Has a note"
            >
              ✎
            </span>
          )}
        </span>

        <span className={styles['measurements']}>
          {entry.measurements.map((measurement) => {
            const track = tracks.get(measurement.trackId)
            if (!track) {
              // The track was hard-deleted, which only happens when it had no
              // measurements — so this should be unreachable. Rendering the id
              // beats rendering nothing if it ever is not.
              return (
                <span key={measurement.trackId} className={styles['measurement']}>
                  <span className={styles['value']}>{String(measurement.value)}</span>
                </span>
              )
            }

            const optionColour =
              isEnumKind(track.kind) && typeof measurement.value === 'string'
                ? track.kind.options.find((option) => option.id === measurement.value)
                    ?.colour
                : undefined

            return (
              <span
                key={measurement.trackId}
                className={`${styles['measurement']} ${track.archived ? styles['archivedTrack'] : ''}`}
              >
                <span
                  className={styles['swatch']}
                  style={{ background: optionColour ?? trackColour(track.id) }}
                  aria-hidden="true"
                />
                {track.name}{' '}
                <span className={styles['value']}>
                  {/* Resolved through the track's own configuration, so a
                      boolean reads as its label and an enum as its option —
                      including one archived since. */}
                  {describeValue(track, measurement.value) ?? String(measurement.value)}
                </span>
                {track.unit !== undefined && <span>{track.unit}</span>}
              </span>
            )
          })}
        </span>

        {entry.tagIds.length > 0 && (
          <span className={styles['entryTags']}>
            {entry.tagIds.map((tagId) => {
              const tag = tags.get(tagId)
              if (!tag) {
                return null
              }
              return (
                <span key={tagId} className={styles['tag']}>
                  {tag.colour !== undefined && (
                    <span
                      className={styles['swatch']}
                      style={{ background: tag.colour }}
                      aria-hidden="true"
                    />
                  )}
                  {tag.name}
                </span>
              )
            })}
          </span>
        )}
      </button>
    </div>
  )
}
